<template>
	<div class="flex items-center">
		<div
			v-for="index in 5"
			:key="index"
			class="relative w-3.5 h-3.5 mr-1"
			:style="{ width: `${(size || 16)}px`, height: `${(size || 16)}px` }"
		>
			<!-- Background star (gray) -->
			<svg
				viewBox="0 0 13 13"
				class="w-full h-full absolute top-0 left-0"
				:fill="backgroundColor"
			>
				<path d="M5.80262 0.452219L4.21589 3.81352L0.665805 4.35428C0.0291704 4.45075 -0.225969 5.27076 0.235712 5.74043L2.80412 8.35534L2.19664 12.0492C2.0873 12.7169 2.76038 13.217 3.31312 12.9048L6.5 11.1607L9.67588 12.9048C10.2396 13.2145 10.9127 12.7169 10.8034 12.0492L10.1959 8.35534L12.7643 5.74043C13.226 5.27076 12.9708 4.45075 12.3342 4.35428L8.78411 3.81352L7.19738 0.452219C6.91308 -0.146926 6.08935 -0.154542 5.80262 0.452219Z" />
			</svg>

			<!-- Foreground star with clip mask (gold) -->
			<div
				class="absolute top-0 left-0 h-full w-0 overflow-hidden"
				:style="getFillStyle(index)"
			>
				<svg
					viewBox="0 0 13 13"
					class="w-full h-full absolute top-0 left-0"
					:fill="starColor"
				>
					<path d="M5.80262 0.452219L4.21589 3.81352L0.665805 4.35428C0.0291704 4.45075 -0.225969 5.27076 0.235712 5.74043L2.80412 8.35534L2.19664 12.0492C2.0873 12.7169 2.76038 13.217 3.31312 12.9048L6.5 11.1607L9.67588 12.9048C10.2396 13.2145 10.9127 12.7169 10.8034 12.0492L10.1959 8.35534L12.7643 5.74043C13.226 5.27076 12.9708 4.45075 12.3342 4.35428L8.78411 3.81352L7.19738 0.452219C6.91308 -0.146926 6.08935 -0.154542 5.80262 0.452219Z" />
				</svg>
			</div>
		</div>
		<div
			v-if="showRating"
			class="ml-2 text-highlighted font-semibold text-base mt-[4px]"
		>
			{{ (rating || 0).toFixed(1) }}
		</div>
	</div>
</template>

<script setup lang="ts">
import type { StarRatingProps } from '@/types/components/star-rating'

const props = defineProps({
	rating: {
		type: Number as PropType<StarRatingProps['rating']>,
		default: 0,
		validator: (value: number) => {
			return value >= 0 && value <= 5
		}
	},
	starColor: {
		type: String as PropType<StarRatingProps['starColor']>,
		default: '#FFD700' // Gold color
	},
	backgroundColor: {
		type: String as PropType<StarRatingProps['backgroundColor']>,
		default: '#E0E0E0' // Light gray
	},
	size: {
		type: Number as PropType<StarRatingProps['size']>,
		default: 16 // Default size in pixels
	},
	showRating: {
		type: Boolean as PropType<StarRatingProps['showRating']>,
		default: true // Show the rating number by default
	}
})

const {
	rating, starColor, backgroundColor, size, showRating
} = toRefs(props)

const getFillStyle = (starIndex: number) => {
	const starRating = rating.value || 0
	const fillPercentage = Math.max(0, Math.min(100, (starRating - (starIndex - 1)) * 100))

	return {
		width: `${fillPercentage}%`,
		overflow: 'hidden'
	}
}
</script>
